import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_DASHBOARD_WIDGET_LIBRARY_PATH,
  GetDashboardWidgetLibraryErrorDto,
  GetDashboardWidgetLibrarySuccessDto,
  Widget,
  WidgetType,
} from '@/api/rest/resources/types/widget';
import { DEFAULT_WIDGET_PROPS_BY_WIDGET_NAME } from '@/pages/shared/hooks/useDefaultWidgetProps';

type SuccessDto = GetDashboardWidgetLibrarySuccessDto;
type ErrorDto = GetDashboardWidgetLibraryErrorDto;

const useWidgetLibrary = () => {
  const queryFn = async () => {
    return getRequest<SuccessDto>(GET_DASHBOARD_WIDGET_LIBRARY_PATH);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['widget'],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useWidgetByNameType = (name: string, type: WidgetType): any => {
  const widgetLibrary = useWidgetLibrary().data;

  const defaultWidget = useDefaultWidgetByName(name);

  const widgets = widgetLibrary.filter((widget: Widget) => widget.name === name && widget.type === type);

  // if widget name is "default" and the config does not return an override for "default",
  // then we check return the default widget config stored in FE
  // eg: show default dashboard, if no custom config found for the organization
  if (widgets.length === 0 && name.includes('default')) {
    return defaultWidget;
  }

  if (widgets.length === 0) {
    return undefined;
  }

  if (widgets.length > 1) {
    return undefined;
  }

  return widgets.at(0);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useDefaultWidgetByName = (name: string): any => {
  if (!(name in DEFAULT_WIDGET_PROPS_BY_WIDGET_NAME)) {
    return undefined;
  }

  const getterWidgetProps =
    DEFAULT_WIDGET_PROPS_BY_WIDGET_NAME[`${name}` as keyof typeof DEFAULT_WIDGET_PROPS_BY_WIDGET_NAME];
  return getterWidgetProps();
};
